import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Camera, Calendar, MapPin, Users } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import dict from "../../i18n/es-MX";

const STATUS_COLOR = {
    programado: "border-slate-600 text-slate-300",
    en_progreso: "border-brand-orange text-brand-orange",
    completado: "border-emerald-500/60 text-emerald-300",
};
import { fmtDate } from "../../lib/dateFormat";

export default function OperatorJobDetail() {
    const { id } = useParams();
    const [job, setJob] = useState(null);
    const [assets, setAssets] = useState([]);
    const [operators, setOperators] = useState([]);
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showCaptureModal, setShowCaptureModal] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/jobs/my");
                const j = r.data.find((x) => x.id === id);
                if (!j) { setError(dict.common.loadError); return; }
                setJob(j);
                // Best-effort: we have asset_ids/operator_ids but no detail endpoints
                // for operators on those sets. Show ids; in Phase 4 we'll add an
                // operator-scoped lookup that returns nicknames.
            } catch (e) { setError(formatApiError(e)); }
            finally { setLoading(false); }
        })();
    }, [id]);

    if (loading) return <div className="px-4 py-10 text-center"><Loader2 className="animate-spin inline text-slate-500"/></div>;
    if (error || !job) return <div className="px-4 py-10 text-center text-slate-500">{error || dict.common.loadError}</div>;

    return (
        <div data-testid="operator-job-detail" className="px-4 py-5 space-y-4">
            <Link to="/campo/trabajos" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100"><ArrowLeft size={14}/>{dict.jobs.operator.myJobs}</Link>

            <header>
                <div className="font-mono text-xs text-slate-500">{job.code}</div>
                <h1 className="font-display text-2xl font-extrabold tracking-tight mt-1">{job.client_name || "—"}</h1>
                <span className={`inline-block mt-2 px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider border ${STATUS_COLOR[job.status]||""}`} data-testid={`detail-status-${job.status}`}>
                    {dict.jobs.statusLabel[job.status]}
                </span>
            </header>

            <section className="rounded-xl bg-slate-900/60 border border-slate-800 p-4 space-y-3">
                <p className="text-sm text-slate-200">{job.description}</p>
                <div className="flex items-center gap-2 text-xs text-slate-400"><MapPin size={14} className="text-brand-orange"/>{job.location_text}</div>
                <div className="flex items-center gap-2 text-xs text-slate-400"><Calendar size={14} className="text-brand-orange"/>{fmtDate(job.start_date)}{job.end_date && ` → ${fmtDate(job.end_date)}`}</div>
            </section>

            {job.external_pay_mode === "por_viaje" && job.my_minimo_viajes != null && (
                <section data-testid="op-pay-mode-banner" className="rounded-xl border border-brand-orange/40 bg-brand-orange/5 p-3 text-sm text-amber-100">
                    {dict.jobs.info.payModeOperatorNotice.replace("{n}", job.my_minimo_viajes)}
                    {typeof job.my_viajes_realizados === "number" && (
                        <span className="block mt-1 text-xs text-slate-300">
                            {dict.jobs.fields.viajesRealizados}: <span className="font-mono text-slate-100">{job.my_viajes_realizados}</span> / {job.my_minimo_viajes}
                        </span>
                    )}
                </section>
            )}

            {job.asset_ids.length > 0 && (
                <section className="rounded-xl bg-slate-900/60 border border-slate-800 p-4">
                    <h2 className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">{dict.jobs.sections.assets}</h2>
                    <div className="flex flex-wrap gap-2">
                        {job.asset_ids.map((aid, i) => <span key={aid} data-testid={`op-asset-${aid}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-slate-800 border border-slate-700 text-slate-200">{job.asset_nicknames?.[i] || aid.slice(0,8)}</span>)}
                    </div>
                </section>
            )}

            {job.operator_ids.length > 1 && (
                <section className="rounded-xl bg-slate-900/60 border border-slate-800 p-4">
                    <h2 className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><Users size={12}/>{dict.jobs.operator.coworkers}</h2>
                    <div className="flex flex-wrap gap-2">
                        {job.operator_ids.map((oid, i) => <span key={oid} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-slate-800 border border-slate-700 text-slate-200">{job.operator_full_names?.[i] || oid.slice(0,8)}</span>)}
                    </div>
                </section>
            )}

            {job.material_assignments?.length > 0 && (
                <section className="rounded-xl bg-slate-900/60 border border-slate-800 p-4">
                    <h2 className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">{dict.jobs.sections.materials}</h2>
                    <ul className="text-sm space-y-1">{job.material_assignments.map((mt,i) => {
                        const unitKey = job.material_units?.[i];
                        const unitLabel = unitKey ? (dict.materials.units[unitKey] || unitKey) : "";
                        return <li key={i} className="text-slate-300">{job.material_names?.[i] || mt.material_id.slice(0,8)} — <span className="text-brand-orange">{mt.quantity}</span>{unitLabel && <span className="text-slate-400"> {unitLabel}</span>}</li>;
                    })}</ul>
                </section>
            )}

            {job.status === "en_progreso" && (
                <Link
                    to={`/campo/trabajos/${job.id}/registro`}
                    data-testid="start-capture-button"
                    className="w-full min-h-[64px] rounded-2xl bg-brand-orange hover:bg-orange-400 text-slate-950 font-semibold uppercase tracking-wider text-base flex items-center justify-center gap-3 transition shadow-[0_4px_0_rgba(0,0,0,0.4)] active:translate-y-[2px] active:shadow-[0_2px_0_rgba(0,0,0,0.4)]"
                >
                    <Camera size={22}/>{dict.jobs.operator.startCapture}
                </Link>
            )}

            {showCaptureModal && (
                <div className="hidden"/>
            )}
        </div>
    );
}
